export const VariationDtoDescriptions = {
  id: "Server-generated. Read-only.",
  experienceId: "References the parent experience.",
  name: "Display name (e.g., 'Control', 'Variant A'). Unique within the experience.",
  percentage:
    "Traffic allocation weight. All percentages across sibling variations must sum to 100.",
  isControl:
    "The baseline variation all others are measured against. Exactly one per experience.",
  order: "Display order in the analytics dashboard. 0-indexed.",
  priceChange:
    "Price adjustment amount for pricing-type experiences. Positive increases price, negative discounts. Use priceChange for simple price A/B tests; use offer for structured discount campaigns (tiered, BOGO, free shipping).",
  priceChangeUnit:
    "How priceChange is applied: as a percentage of the original price or a fixed monetary amount.",
  priceRoundingType:
    "Rounding strategy applied after priceChange (e.g., round up, round down, charm pricing).",
  priceRoundingAmount:
    "Target value for rounding (e.g., 0.99 for charm pricing like $X.99).",
  onsiteEdits:
    "DOM content modifications (text, HTML, image swaps, element removal). For CSS styling changes, use onsiteInjections instead.",
  onsiteInjections:
    "CSS styling and custom JS injection. At most one per variation. For DOM content changes (text, images, HTML), use onsiteEdits instead.",
  offer:
    "Structured discount configuration (tiered volume discounts, cart-level discounts, gift-with-purchase, free shipping). Use offer for campaign/discount experiences; use priceChange for simple price A/B tests.",
  offerId:
    "Server-generated. Read-only. References the associated offer. Populated when offer is set.",
  shippingRateGroups:
    "Shipping rate overrides. Only populated for shipping test experiences.",
  excludeProducts:
    "When true, specific products are excluded from price changes. Use excludeProducts when most products should be tested and only a few excluded. Mutually exclusive with includeProducts.",
  includeProducts:
    "When true, only specific products receive price changes. Use includeProducts when only a few products should be tested. Mutually exclusive with excludeProducts.",
  testExperienceId:
    "Links to another experience for advanced multi-experience configurations.",
  functionSyncError:
    "Server-generated. Read-only. Indicates a sync failure with Shopify Functions for this variation's discount.",
  redirects: "URL redirect rules for content/url experiences.",
  userInterfaces: "UI widget configurations displayed for this variation.",
  checkoutBlocks:
    "Shopify checkout extensibility block configurations for this variation.",
  cartTargeting:
    "Cart-based targeting rules that determine when this variation is shown based on cart contents.",
  components:
    "Onsite component configurations (e.g. onsite upsells) rendered on the storefront for this variation.",
} as const;

export const CreateVariationDtoDescriptions = {
  experienceId:
    "Omit when creating as part of a new experience -- auto-assigned from parent.",
  name: "Display name (e.g., 'Control', 'Variant A'). Must be unique across variations.",
  percentage:
    "Traffic allocation weight. All percentages across variations must sum to 100.",
  isControl:
    "Exactly one variation per experience must be control. Others are measured against it.",
  order:
    "Display order in analytics. Must be unique across variations. 0-indexed.",
  onsiteEdits:
    "ONLY for DOM content changes (text replacement, HTML insertion, image swaps, element removal). Do NOT use for visual styling changes (colors, backgrounds, fonts, layout) — use onsiteInjection.customCss instead. Leave as empty array when only CSS/JS changes are needed.",
  onsiteInjection:
    "CSS styling (customCss) and custom JS (customJs). For visual styling changes (background-color, font-size, colors, borders, etc.), use ONLY this field and leave onsiteEdits as an empty array. For DOM content changes (text, images, HTML), use onsiteEdits instead. Note: singular field name.",
  offer: "Discount offer configuration. Null for no offer.",
  redirects: "URL redirects. Only used with content/url experience type.",
  shippingRateGroups: "Only relevant for shipping test experiences.",
} as const;

export const ExternalVariationDescriptions = {
  id: "Copy from GET response. Omitting a variation from the array deletes it.",
  priceChange:
    "Group-level price transform for a pricing-type experience. Set on each non-control test group to define its price change relative to each product's base price (e.g. '10' with priceChangeUnit 'percent' for +10%, or '-5' for a discount). Leave null/empty on the control group so it keeps the base price. This defines the group's transform only; NO products are attached here. REQUIRED NEXT STEP: after this create returns an experienceId, call prepare_seed_price_test_products with that id and the chosen product variants to attach them and compute per-variant prices — it reads this transform back off the experience. A pricing experience created here has no products until you run that seed step.",
  priceChangeUnit:
    "How priceChange is applied: 'percent' (a percentage of the base price) or 'dollar' (a fixed monetary amount).",
  priceRoundingType:
    "Rounding applied after priceChange: 'none', 'nearest', 'up', or 'down' (e.g. 'down' with priceRoundingAmount '0.99' for charm pricing).",
  priceRoundingAmount:
    "Target value for rounding, e.g. '0.99' for charm pricing like $X.99. Only meaningful when priceRoundingType is not 'none'.",
  includeProducts:
    "Flag (not a product list). When true, only specific products receive the price change. Mutually exclusive with excludeProducts. Products themselves are attached in the seed step, not here.",
  excludeProducts:
    "Flag (not a product list). When true, specific products are excluded from the price change. Mutually exclusive with includeProducts. Products themselves are attached in the seed step, not here.",
  onsiteEdits:
    "Full replacement — include ALL edits to keep. Omitted edits are deleted. ONLY for DOM content changes (text replacement, HTML insertion, image swaps, element removal). Do NOT create onsiteEdits for visual styling changes like colors, backgrounds, fonts, or layout — use onsiteInjection.customCss instead. Leave as empty array when only CSS/JS changes are needed.",
  onsiteInjection:
    "Full replacement. Set to null to remove. Use for all visual styling changes (background-color, font-size, colors, borders, layout, etc.) via customCss, and custom behavior via customJs. CSS-only changes should use ONLY this field with an empty onsiteEdits array — do not create a corresponding onsiteEdit.",
} as const;
